import { useEffect, useState, useContext, useRef } from "react";

import { ChevronLeft, ChevronRight } from "../../files/icons/index.js";

import Drop from "./drop.js";

import { AppContext } from '../../context.js';

import { DateToFormat, parseDateYMD } from "../../utils/datetime.js";

// Compare dates by calendar day only (ignore time-of-day).
function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function isAfterMaxDate(date, maxDate) {
  if (!date || !maxDate) return false;
  return startOfDay(date) > startOfDay(maxDate);
}

function DatePicker(props) {
  const { date, onChange, onChangeID, leftText, leftIcon, rightIcon, className, defaultLevel = "day", defaultYear, maxDate, disabled = false } = props;

  const context = useContext(AppContext);

  const [dateD, setDateD] = useState(null);

  // const [passedDate, setPassedDate] = useState(new Date());

  const [closeDateDrop, setCloseDateDrop] = useState(0);
  
  useEffect(() => {
    setDateD(date ? parseDateYMD(date) : null);
  }, [date]);

  function dateSelected(newDate) {
    if (disabled) return;
    //setDate(newDate);
    if (onChange) { 
      if (onChangeID !== undefined && onChangeID !== null) {
        onChange(newDate, onChangeID);
      } else {
        onChange(newDate);
      }
    }
    setCloseDateDrop(closeDateDrop + 1);
  }

  const trigger = (
    <div className={"item grey " + className} style={disabled ? { opacity: 0.7, cursor: "default" } : undefined}>
      {leftIcon && leftIcon}
      {leftText && <span className="typo-normal-150">{leftText}</span>}
      <span className="typo-normal-150">{dateD ? DateToFormat(dateD, context.dateFormat) : "not set"}</span>
      {!disabled && rightIcon}
    </div>
  );

  if (disabled) {
    return trigger;
  }

  return (
    <Drop close={closeDateDrop}>
      {trigger}
      <div className="card shadowmore" style={{width: 'fit-content'}}>
        <div className="card-header sm">
          <DateSelector date={dateD} dateSelected={dateSelected} defaultLevel={defaultLevel} defaultYear={defaultYear} maxDate={maxDate} />
        </div>
      </div>
    </Drop>
  );
}

function SlotPicker(props) {
  const { userList, userID, onChange, onChangeID, leftText, rightIcon, className } = props;

  const context = useContext(AppContext);

  const [date, setDate] = useState(new Date());

  const [slots, setSlots] = useState([
    {start: '08:00', end: '09:00'},
    {start: '09:00', end: '10:00'},
    {start: '10:00', end: '11:00'},
    {start: '11:00', end: '12:00'},
    {start: '12:00', end: '13:00'},
    {start: '13:00', end: '14:00'},
    {start: '14:00', end: '15:00'},
    {start: '15:00', end: '16:00'},
  ]);

  const [closeDateDrop, setCloseDateDrop] = useState(0);
  
  function dateSelected(newDate) {
    setDate(newDate);
  }

  return (
    <Drop close={closeDateDrop}>
      <div className={"item grey " + className}>
        {leftText && <span className="typo-normal-150">{leftText}</span>}
        <span className="typo-normal-150"> {"25.10.2024 | 09:00 - 10:00" }</span>
        {rightIcon && rightIcon}
      </div>
      <div className="card shadowmore" style={{width: 'fit-content'}}>
        <div className="card-header sm">
          <div className="d-flex justify-content-between align-items-top">
            <DateSelector date={date} dateSelected={dateSelected} />
            
            <div className="ms-3 text-center" style={{width: 120, borderLeft: '1px solid lightgrey'}}>
              <div className="item typo-normal-125 mb-2">{date.getDate()} {date.toLocaleString('default', { month: 'long' })} {date.getFullYear()}</div>
              {slots.map((slot) => {
                return (
                  <div key={"slot_"+slot.start} className="item">
                    <span className="typo-normal-150">{slot.start} - {slot.end}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </Drop>
  );
}

function DateSelector(props) {
  const { date, dateSelected, defaultLevel, defaultYear, maxDate } = props;

  const context = useContext(AppContext);

  const [selectedDay, setSelectedDay] = useState(date ? date : null);
  const [visibleDay, setVisibleDay] = useState(date ? date : new Date());
  const [currentDay, setCurrentDay] = useState(new Date());

  const [level, setLevel] = useState(defaultLevel ? defaultLevel : "day"); // day, month, year

  const [gridDays, setGridDays] = useState([]);

  const [gridMonths, setGridMonths] = useState([
    ['Q1', 'Q2', 'Q3', 'Q4'],
    [0, 3, 6, 9],
    [1, 4, 7, 10],
    [2, 5, 8, 11],
  ]);

  const [gridYears, setGridYears] = useState([]);

  const monthAbbreviations = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  useEffect(() => {
    setSelectedDay(date);
    // If no date is provided but defaultYear is passed, set visible day to that year
    if (!date && defaultYear) {
      setVisibleDay(new Date(defaultYear, 0, 1));
    }
  }, [date, defaultYear]);

  useEffect(() => {
    const year = visibleDay.getFullYear();
    const month = visibleDay.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    //console.log(`Number of days in month: ${daysInMonth}`);
    const firstDay = new Date(year, month, 1).getDay();
    //console.log(`First day of month: ${firstDay}`);
    const lastDay = new Date(year, month, daysInMonth).getDay();
    //console.log(`Last day of month: ${lastDay}`);

    // Create days grid
    const daysGrid = [
      ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
    ];

    let row = [];
    for (let i = 0; i < firstDay; i++) {
      row.push('');
    }
    for (let i = 1; i <= daysInMonth; i++) {
      row.push(i);
      if (row.length === 7) {
        daysGrid.push(row);
        row = [];
      }
    }
    if (row.length > 0) {
      while (row.length < 7) {
        row.push('');
      }
      daysGrid.push(row);
    }

    setGridDays(daysGrid);

    // Create years grid
    const yearsGrid = [[], [], [], [], []];

    let firstYear = year;
    while (firstYear % 5 !== 0) {
        firstYear--;
    }

    let yearsBreakpoints = [firstYear-10, firstYear-5, firstYear, firstYear+5, firstYear+10];
    
    for (let i = 0; i < 5; i++) {
      for (let j = 0; j < 5; j++) {
        yearsGrid[j].push(yearsBreakpoints[i] + j);
      }
    }

    yearsGrid.unshift(yearsBreakpoints.map((b) => {
      let lastDigits = b % 100;
      return `'` + (lastDigits < 10 ? '0' + lastDigits : lastDigits);
    }));

    setGridYears(yearsGrid);
  }, [visibleDay]);

  function prev() {
    if (level === "day") {
      setVisibleDay(new Date(visibleDay.getFullYear(), visibleDay.getMonth() - 1, 1));
    } else if (level === "month") {
      setVisibleDay(new Date(visibleDay.getFullYear() - 1, 0, 1));
    } else if (level === "year") {
      setVisibleDay(new Date(visibleDay.getFullYear() - 10, 0, 1));
    }
  }

  function next() {
    if (level === "day") {
      setVisibleDay(new Date(visibleDay.getFullYear(), visibleDay.getMonth() + 1, 1));
    } else if (level === "month") {
      setVisibleDay(new Date(visibleDay.getFullYear() + 1, 0, 1));
    } else if (level === "year") {
      setVisibleDay(new Date(visibleDay.getFullYear() + 10, 0, 1));
    }
  }

  function selectDay(i, j) {
    let d = new Date(visibleDay.getFullYear(), visibleDay.getMonth(), gridDays[i][j]);
    if (isAfterMaxDate(d, maxDate)) return;
    setSelectedDay(d);
    dateSelected(d);
  }

  function selectMonth(i) {
    let d = new Date(visibleDay.getFullYear(), i, 1);
    setVisibleDay(d);
    setLevel("day");
  }

  function selectYear(i) {
    let d = new Date(i, visibleDay.getMonth(), 1);
    setVisibleDay(d);
    setLevel("month");
  }

  function levelUp() {
    if (level === "day") {
      setLevel("month");
    } else if (level === "month") {
      setLevel("year");
    }
  }

  function renderCalendarTitle() {
    if (level === "day") {
      return (
        <div onClick={levelUp} className="item">
          <span className="typo-normal-125">{visibleDay.toLocaleString('default', { month: 'long' })} {visibleDay.getFullYear()}</span>
        </div>
      );
    }

    if (level === "month") {
      return (
        <div onClick={levelUp} className="item">
          <span className="typo-normal-125">year {visibleDay.getFullYear()}</span>
        </div>
      );
    }

    let firstYear = visibleDay.getFullYear();
    while (firstYear % 5 !== 0) {
      firstYear--;
    }

    return (
      <span className="typo-normal-125">{firstYear-10} - {firstYear+14}</span>
    );
  }

  return (
    <div style={{width: 220}}>

      {/* Calendar header */}
      <div className="d-flex justify-content-between align-items-center">
        {/* Arrow left */}
        <div onClick={prev} className="item">
          <ChevronLeft height={16} width={16} color={'grey'} />
        </div>

        {/* Calendar title */}
        {renderCalendarTitle()}

        {/* Arrow right */}
        <div onClick={next} className="item">
          <ChevronRight height={16} width={16} color={'grey'} />
        </div>
      </div>

      {/* Calendar body */}
      {level === "day" && (
        <div className="datepicker-days">
          {gridDays.map((row, i) => {
            return (
              <div className={"datepicker-row d-flex justify-content-between align-items-center " + (i === 0 ? "header" : "")}>
                {row.map((day, j) => {
                  let className = "datepicker-day typo-normal-150";
                  const cellDate = (i !== 0 && day !== '')
                    ? new Date(visibleDay.getFullYear(), visibleDay.getMonth(), day)
                    : null;
                  const disabled = cellDate && isAfterMaxDate(cellDate, maxDate);
                  if (i === 0) {
                    className += " header ";
                  }
                  if (i !== 0 && day !== '' && !disabled) {
                    className += " clickable ";
                  }
                  if (disabled) {
                    className += " disabled ";
                  }
                  if (day === currentDay.getDate() && visibleDay.getMonth() === currentDay.getMonth() && visibleDay.getFullYear() === currentDay.getFullYear()) {
                    className += " today ";
                  }
                  if (selectedDay && day === selectedDay.getDate() && visibleDay.getMonth() === selectedDay.getMonth() && visibleDay.getFullYear() === selectedDay.getFullYear()) {
                    className += " active ";
                  }
                  return (
                    <div key={i + '-' + j} className={className} onClick={() => !disabled && selectDay(i, j)}>
                      {day}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
      
      {level === "month" && (
        <div className="datepicker-days">
          {gridMonths.map((row, i) => {
            return (
              <div className={"datepicker-row d-flex justify-content-between align-items-center " + (i === 0 ? "header" : "")}>
                {row.map((month, j) => {
                  let className = "datepicker-day typo-normal-150";
                  if (i === 0) {
                    className += " header ";
                  }
                  if (i !== 0 && month !== '') {
                    className += " clickable ";
                  }
                  if (month === currentDay.getMonth() && visibleDay.getFullYear() === currentDay.getFullYear()) {
                    className += " today ";
                  }
                  if (selectedDay && month === selectedDay.getMonth() && visibleDay.getFullYear() === selectedDay.getFullYear()) {
                    className += " active ";
                  }
                  return (
                    <div key={i + '-' + j} className={className} onClick={() => selectMonth(month)}>
                      {i === 0 ? month : monthAbbreviations[month]}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {level === "year" && (
        <div className="datepicker-days">
          {gridYears.map((row, i) => {
            return (
              <div className={"datepicker-row d-flex justify-content-between align-items-center " + (i === 0 ? "header" : "")}>
                {row.map((year, j) => {
                  let className = "datepicker-day typo-normal-150";
                  if (i === 0) {
                    className += " header ";
                  }
                  if (i !== 0 && year !== '') {
                    className += " clickable ";
                  }
                  if (year === currentDay.getFullYear()) {
                    className += " today ";
                  }
                  if (selectedDay && year === selectedDay.getFullYear() && visibleDay.getFullYear() === selectedDay.getFullYear()) {
                    className += " active ";
                  }
                  return (
                    <div key={i + '-' + j} className={className} onClick={() => selectYear(year)}>
                      {year}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EditableDatePicker(props) {
  const { date, onChange, onChangeID, leftText, leftIcon, rightIcon, className, defaultLevel = "day", defaultYear, placeholder = "not set", clearable = true, disabled = false, maxDate } = props;

  const context = useContext(AppContext);

  const [dateD, setDateD] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [isValidInput, setIsValidInput] = useState(true);

  const [closeDateDrop, setCloseDateDrop] = useState(0);

  const inputRef = useRef(null);
  const cursorPositionRef = useRef(null);

  const effectiveClearable = clearable && !disabled;

  useEffect(() => {
    const newDate = date ? parseDateYMD(date) : null;
    setDateD(newDate);
    setInputValue(newDate ? DateToFormat(newDate, context.dateFormat) : "");
    setIsValidInput(true);
  }, [date, context.dateFormat]);

  // Restore cursor position after input value changes
  useEffect(() => {
    if (cursorPositionRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
      cursorPositionRef.current = null;
    }
  }, [inputValue]);

  function dateSelected(newDate) {
    if (disabled) return;
    if (isAfterMaxDate(newDate, maxDate)) return;
    if (onChange) {
      if (onChangeID !== undefined && onChangeID !== null) {
        onChange(newDate, onChangeID);
      } else {
        onChange(newDate);
      }
    }
    setCloseDateDrop(closeDateDrop + 1);
  }

  // Parse date string based on format
  function parseDate(dateString, format) {
    if (!dateString || !dateString.trim()) {
      return null;
    }

    // Default format
    if (!format || format === 'us') {
      format = 'MM/DD/YYYY';
    }

    // Remove extra spaces
    dateString = dateString.trim();

    // Validate digit count - must be exactly 8 digits (MM/DD/YYYY = 2+2+4)
    const digitsOnly = dateString.replace(/[^0-9]/g, '');
    if (digitsOnly.length !== 8) {
      return null;
    }

    // Extract day, month, year positions from format
    const formatParts = format.split(/[\/\-\. ]/);
    const dateParts = dateString.split(/[\/\-\. ]/);

    if (dateParts.length !== formatParts.length) {
      return null;
    }

    let day, month, year;

    for (let i = 0; i < formatParts.length; i++) {
      const part = formatParts[i];
      const value = parseInt(dateParts[i], 10);

      if (isNaN(value)) {
        return null;
      }

      if (part === 'DD') {
        day = value;
      } else if (part === 'MM') {
        month = value;
      } else if (part === 'YYYY') {
        year = value;
      }
    }

    // Validate ranges
    if (!day || !month || !year) {
      return null;
    }

    if (month < 1 || month > 12) {
      return null;
    }

    if (day < 1 || day > 31) {
      return null;
    }

    // Create date and validate it's real (e.g., not Feb 31)
    const parsedDate = new Date(year, month - 1, day);

    if (parsedDate.getDate() !== day ||
        parsedDate.getMonth() !== month - 1 ||
        parsedDate.getFullYear() !== year) {
      return null;
    }

    return parsedDate;
  }

  function handleInputChange(e) {
    if (disabled) return;
    let value = e.target.value;
    const cursorPos = e.target.selectionStart;

    // Get the separator from the format
    let format = context.dateFormat;
    if (!format || format === 'us') {
      format = 'MM/DD/YYYY';
    }
    const separatorMatch = format.match(/[\/\-\. ]/);
    const separator = separatorMatch ? separatorMatch[0] : '/';

    // Count total digits in old value
    const oldDigitsCount = inputValue.replace(/[^0-9]/g, '').length;

    // Count digits before cursor in the old value
    const digitsBeforeCursor = inputValue.substring(0, cursorPos).replace(/[^0-9]/g, '').length;

    // Remove any non-digit and non-separator characters
    const digitsAndSeparators = value.replace(new RegExp(`[^0-9${separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`, 'g'), '');

    // Get only digits for counting
    const digitsOnly = digitsAndSeparators.replace(/[^0-9]/g, '');

    // Detect if a digit was added
    const digitAdded = digitsOnly.length > oldDigitsCount;

    // Auto-format: add separators at positions 2 and 4
    let formattedValue = '';
    for (let i = 0; i < digitsOnly.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formattedValue += separator;
      }
      formattedValue += digitsOnly[i];
    }

    // Calculate new cursor position based on digits before cursor
    let newCursorPos = 0;
    let digitCount = 0;
    let targetDigits = digitAdded ? digitsBeforeCursor + 1 : digitsBeforeCursor;

    for (let i = 0; i < formattedValue.length; i++) {
      if (formattedValue[i] >= '0' && formattedValue[i] <= '9') {
        digitCount++;
      }
      if (digitCount >= targetDigits) {
        newCursorPos = i + 1;
        break;
      }
    }

    // Store cursor position to be restored after state update
    cursorPositionRef.current = newCursorPos;

    setInputValue(formattedValue);

    if (!formattedValue.trim()) {
      // Empty input — clear unless clearable is false (restore on blur).
      if (!effectiveClearable) {
        setIsValidInput(true);
        return;
      }
      setIsValidInput(true);
      setDateD(null);
      if (onChange) {
        if (onChangeID !== undefined && onChangeID !== null) {
          onChange(null, onChangeID);
        } else {
          onChange(null);
        }
      }
      return;
    }

    // Try to parse the date
    const parsedDate = parseDate(formattedValue, context.dateFormat);

    if (parsedDate && !isAfterMaxDate(parsedDate, maxDate)) {
      setIsValidInput(true);
      setDateD(parsedDate);
      if (onChange) {
        if (onChangeID !== undefined && onChangeID !== null) {
          onChange(parsedDate, onChangeID);
        } else {
          onChange(parsedDate);
        }
      }
    } else {
      setIsValidInput(false);
    }
  }

  function handleInputBlur() {
    // On blur, if input is invalid or emptied while not clearable, reset to the current date.
    if (!isValidInput || (!effectiveClearable && !inputValue.trim())) {
      setInputValue(dateD ? DateToFormat(dateD, context.dateFormat) : "");
      setIsValidInput(true);
    }
  }

  function handleInputKeyDown(e) {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }

  const trigger = (
    <div className={"item grey " + className} style={disabled ? { opacity: 0.7, cursor: "default" } : undefined}>
      {leftIcon && leftIcon}
      {leftText && <span className="typo-normal-150">{leftText}</span>}
      <input
        ref={inputRef}
        type="text"
        className="typo-normal-150"
        style={{
          border: 'none',
          background: 'transparent',
          outline: 'none',
          width: '100px',
          padding: '0',
          cursor: disabled ? 'default' : undefined,
        }}
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        placeholder={placeholder}
        readOnly={disabled}
        disabled={disabled}
      />
      {!disabled && rightIcon}
    </div>
  );

  // Locked: show value only — no calendar drop.
  if (disabled) {
    return trigger;
  }

  return (
    <Drop close={closeDateDrop}>
      {trigger}
      <div className="card shadowmore" style={{width: 'fit-content'}}>
        <div className="card-header sm">
          <DateSelector date={dateD} dateSelected={dateSelected} defaultLevel={defaultLevel} defaultYear={defaultYear} maxDate={maxDate} />
        </div>
      </div>
    </Drop>
  );
}

export { DatePicker, SlotPicker, DateSelector, EditableDatePicker };